import { DailyMeasurement } from 'features/forecasts/types';
import { MeasurementData } from '../types';

export const convertDBNumber = (
  num: number | string | undefined | null,
  unit: string
) => {
  if (num === undefined || num === null) return 'Not available';
  return appendUnitsToNumber((Number(num) / 100).toFixed(2), unit);
};

export const convertDBNumberNoDecimals = (
  num: number | string | undefined | null,
  unit: string
) => {
  if (num === undefined || num === null) return 'Not available';
  return appendUnitsToNumber((Number(num) / 100).toFixed(0), unit);
};

export const convertDBNumberNoDecimalsNoUnits = (
  num: number | string | undefined | null
) => {
  if (num === undefined || num === null) return 'Not available';
  return (Number(num) / 100).toFixed(0);
};

export const appendUnitsToNumber = (
  num: number | null | undefined | string,
  unit: string
) => {
  if (num === undefined || num === null) return 'Not available';
  return num + ' ' + unit;
};

export const convertNumberFromDB = (
  num: number | null | undefined,
  dataType: MeasurementData | DailyMeasurement
) => {
  if (num === undefined || num === null) return null;
  if (
    dataType === 'wind_kph' ||
    dataType === 'gust_kph' ||
    dataType === 'temp_c' ||
    dataType === 'precip_mm' ||
    dataType === 'dewpoint_c' ||
    dataType === 'pressure_mb' ||
    dataType === 'maxtemp_c' ||
    dataType === 'mintemp_c' ||
    dataType === 'maxwind_kph' ||
    dataType === 'totalprecip_mm'
  )
    return num / 100;
  return num;
};

export const convertNumberToSaveToDB = (
  num: number | null | undefined,
  dataType: MeasurementData
) => {
  if (num === undefined || num === null) return null;
  if (
    dataType === 'wind_kph' ||
    dataType === 'gust_kph' ||
    dataType === 'temp_c' ||
    dataType === 'precip_mm' ||
    dataType === 'dewpoint_c' ||
    dataType === 'pressure_mb'
  )
    return num * 100;
  return num;
};

export const getLabelForMeasurementType = (
  type: MeasurementData | '',
  useImperialUnits: boolean | null | undefined
): string => {
  if (type === 'wind_kph')
    return 'Speed ' + (useImperialUnits ? '(mph)' : '(kph)');
  if (type === 'gust_kph')
    return 'Gust ' + (useImperialUnits ? '(mph)' : '(kph)');
  if (type === 'temp_c') return 'Temp ' + (useImperialUnits ? '(F)' : '(C)');
  if (type === 'cloud_percent') return 'Cloud Cover (%)';
  if (type === 'precip_mm')
    return 'Precipitation ' + (useImperialUnits ? '(in/h)' : '(mm/h)');
  if (type === 'humidity') return 'Relative Humidity (%)';
  if (type === 'dewpoint_c')
    return 'Dewpoint ' + (useImperialUnits ? '(F)' : '(C)');
  if (type === 'pressure_mb')
    return 'Barometric Pressure ' + (useImperialUnits ? '(inches Hg)' : '(mb)');
  return '';
};

export const getChartTitleForMeasurementType = (
  type: MeasurementData | ''
): string => {
  if (type === 'wind_kph') return 'Wind';
  if (type === 'gust_kph') return 'Wind';
  if (type === 'temp_c') return 'Temperature';
  if (type === 'cloud_percent') return 'Cloud Cover';
  if (type === 'precip_mm') return 'Precipitation';
  if (type === 'humidity') return 'Relative Humidity';
  if (type === 'dewpoint_c') return 'Dewpoint';
  if (type === 'pressure_mb') return 'Barometric Pressure';
  return '';
};

export const getUnitForMeasurementType = (
  type: MeasurementData | DailyMeasurement | '',
  useImperialUnits: boolean | null | undefined
): string => {
  if (type === 'wind_kph' || type === 'gust_kph' || type === 'maxwind_kph')
    return useImperialUnits ? 'mph' : 'kph';
  if (
    type === 'temp_c' ||
    type === 'dewpoint_c' ||
    type === 'maxtemp_c' ||
    type === 'mintemp_c'
  )
    return useImperialUnits ? 'F' : 'C';
  if (type === 'cloud_percent' || type === 'humidity') return '%';
  if (type === 'precip_mm' || type === 'totalprecip_mm')
    return useImperialUnits ? 'in/h' : 'mm/h';
  if (type === 'pressure_mb') return useImperialUnits ? 'inches Hg' : 'mb';
  return '';
};

export const convertKphToMph = (datum: number | null | undefined) => {
  if (datum === null || datum === undefined) return datum;
  return datum * 0.621371;
};

export const convertMphToKph = (datum: number | null | undefined) => {
  if (datum === null || datum === undefined) return datum;
  return datum / 0.621371;
};

export const convertCToF = (datum: number | null | undefined) => {
  if (datum === null || datum === undefined) return datum;
  const result = (datum * 9) / 5 + 32 * 100;
  return result;
};

export const convertFToC = (datum: number | null | undefined) => {
  if (datum === null || datum === undefined) return datum;
  const result = ((datum - 32 * 100) * 5) / 9;
  return result;
};

export const convertMbToInHg = (datum: number | null | undefined) => {
  if (datum === null || datum === undefined) return datum;
  return datum / 33.864;
};

export const convertInHgToMb = (datum: number | null | undefined) => {
  if (datum === null || datum === undefined) return datum;
  return datum * 33.864;
};

export const convertMmToIn = (datum: number | null | undefined) => {
  if (datum === null || datum === undefined) return datum;
  return datum / 25.4;
};

export const convertInToMm = (datum: number | null | undefined) => {
  if (datum === null || datum === undefined) return datum;
  return datum * 25.4;
};

export const convertFromImperial = (
  datum: number | null | undefined,
  type: MeasurementData | DailyMeasurement,
  inImperial: boolean | null | undefined
) => {
  if (!inImperial) return datum;
  if (type === 'wind_kph' || type === 'gust_kph' || type === 'maxwind_kph')
    return convertMphToKph(datum);
  if (
    type === 'maxtemp_c' ||
    type === 'mintemp_c' ||
    type === 'temp_c' ||
    type === 'dewpoint_c'
  )
    return convertFToC(datum);
  if (type === 'cloud_percent' || type === 'humidity') return datum;
  if (type === 'precip_mm') return convertInToMm(datum);
  if (type === 'pressure_mb') return convertInHgToMb(datum);
};

export const convertToImperial = (
  datum: number | null | undefined,
  type: MeasurementData | DailyMeasurement,
  inImperial: boolean | null | undefined
) => {
  if (!inImperial) return datum;
  if (type === 'wind_kph' || type === 'gust_kph' || type === 'maxwind_kph')
    return convertKphToMph(datum);
  if (
    type === 'maxtemp_c' ||
    type === 'mintemp_c' ||
    type === 'temp_c' ||
    type === 'dewpoint_c'
  )
    return convertCToF(datum);
  if (type === 'cloud_percent' || type === 'humidity') return datum;
  if (type === 'precip_mm' || type === 'totalprecip_mm')
    return convertMmToIn(datum);
};
